import React, { useEffect, useRef, useState } from "react";
import { IMedia, LayoutCommonProps } from "../../../types";
import { ArrowLeft, ArrowRight } from 'react-bootstrap-icons';
import * as Styled from "./Layout3.styled";
import { BigCard } from "../../media-card/big-card";
import { SeeCard } from "../../media-card/see-card";
import { SidebarCard } from "../../media-card/small-card";
import LayoutModal from "../../media-card/FullScreenModal";
import { LayoutHeader } from "../LayoutHeader";
import { scrollOnce, startContinuousScroll, stopContinuousScroll } from "../../../libs/utils";
import RatingToast from "../RatingToast";

export const Layout3: React.FC<LayoutCommonProps> = ({
    mediaItems,
    mediaRating,
    onWatchClick,
    onRatingSuccess,
    isSignedIn,
    sessionToken,
    activePlans,
    hasFullAccess,
    freeTrialPlanIds,
    playlistTitle,
    enableCompetitionMode,
    enableRatingControls,
    refreshMediaRatings
}) => {
    const rightScrollRef = useRef<HTMLDivElement>(null);
    const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [mainMedia, setMainMedia] = useState<IMedia | undefined>(undefined);
    const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
    const [showToast, setShowToast] = useState(false);

    const handleRatingSuccessLocal = () => {
        setShowToast(true);
        onRatingSuccess();
        refreshMediaRatings?.()
    };

    useEffect(() => {
        if (mediaItems.length && !mainMedia) {
            const firstMedia = mediaItems[0];
            setMainMedia(firstMedia);
            setSelectedCardId(firstMedia.id);
        }
    }, [mediaItems, mainMedia]);


    const handleSeeAllClick = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const onCardClick = (item: IMedia) => {
        setMainMedia(item);
        setSelectedCardId(item.id);
    };

    if (!mediaItems.length || !mainMedia) return null;

    const remainingMedia = mediaItems;
    return (
        <>
            <LayoutHeader
                title={playlistTitle}
                itemCount={mediaItems?.length}
                onSeeAll={handleSeeAllClick}
                marginBottom="0px"
                padding="50px 0px 0px 0px"
                marginTop="0px"
            />
            <Styled.Container>
                <Styled.BigCard>
                    <BigCard
                        customHeight={'680px'}
                        mediaItem={mainMedia}
                        mediaRating={mediaRating.find((m) => m.mediaId === mainMedia.mediaId) || 0}
                        // mediaRating={
                        //     mediaRating.find((_item) => _item.mediaId === mainMedia.mediaId)
                        // }
                        enableRatingControls={enableRatingControls}
                        isSignedIn={isSignedIn}
                        hasEligiblePlan={
                            isSignedIn &&
                            (!!mainMedia.planDetails?.length
                                ? !!activePlans.filter((plan) =>
                                    mainMedia.planDetails?.some((pd) => pd?.id === plan.id)
                                ).length ||
                                freeTrialPlanIds.includes(mainMedia.planDetails[0]?.id) ||
                                hasFullAccess
                                : true)
                        }
                        onWatchClick={onWatchClick}
                        onRatingSuccess={handleRatingSuccessLocal}
                        sessionToken={sessionToken}
                        onlyShowWatchButton={false}
                        alignInfoRowSmallCenter={true}
                        enableCompetitionMode={enableCompetitionMode}
                    />
                    <RatingToast show={showToast} onClose={() => setShowToast(false)} />
                </Styled.BigCard>

                <Styled.SidebarCard>
                    {/* Arrows */}
                    {remainingMedia.length > 4 && (
                        <div>
                            <Styled.ArrowLeft
                                onClick={() => scrollOnce(rightScrollRef, "left")}
                                onMouseDown={() => startContinuousScroll(rightScrollRef, "left", scrollIntervalRef)}
                                onMouseUp={() => stopContinuousScroll(scrollIntervalRef)}
                                onMouseLeave={() => stopContinuousScroll(scrollIntervalRef)}
                                title="Scroll Left"
                            >
                                <ArrowLeft size={30} />
                            </Styled.ArrowLeft>
                            <Styled.ArrowRight
                                onClick={() => scrollOnce(rightScrollRef, "right")}
                                onMouseDown={() => startContinuousScroll(rightScrollRef, "right", scrollIntervalRef)}
                                onMouseUp={() => stopContinuousScroll(scrollIntervalRef)}
                                onMouseLeave={() => stopContinuousScroll(scrollIntervalRef)}
                                title="Scroll Right"
                            >
                                <ArrowRight size={30} />
                            </Styled.ArrowRight>
                        </div>
                    )}

                    <Styled.SidebarCardContent>
                        <Styled.ScrollableList ref={rightScrollRef} scrollable={remainingMedia.length > 4}>
                            {remainingMedia.slice(0, 6).map((item) => (
                                <Styled.SidebarCardWrapper key={item.id}>
                                    <SidebarCard
                                        mediaItem={item}
                                        cardClick={() => onCardClick(item)}
                                        isSelected={item.id === selectedCardId}
                                        mediaRating={mediaRating.find((m) => m.mediaId === item.mediaId)?.rating || 0}
                                        enableRatingControls
                                        isSignedIn={isSignedIn}
                                        hasEligiblePlan={
                                            isSignedIn &&
                                            (!!item.planDetails?.length
                                                ? !!activePlans.filter((plan) =>
                                                    item.planDetails?.some((pd) => pd?.id === plan.id)
                                                ).length ||
                                                freeTrialPlanIds.includes(item.planDetails[0]?.id) ||
                                                hasFullAccess
                                                : true)
                                        }
                                        onWatchClick={onWatchClick}
                                        onRatingSuccess={onRatingSuccess}
                                        sessionToken={sessionToken}
                                    />
                                </Styled.SidebarCardWrapper>
                            ))}

                            {/* SeeCard at end */}
                            {remainingMedia.length > 6 && (
                                <Styled.SeeAllWrapper onClick={handleSeeAllClick}>
                                    <SeeCard />
                                </Styled.SeeAllWrapper>
                            )}
                        </Styled.ScrollableList>

                    </Styled.SidebarCardContent>
                </Styled.SidebarCard>
            </Styled.Container>

            {/* Modal with Lazy Loaded Cards */}
            <LayoutModal
                title={playlistTitle}
                show={isModalOpen}
                onHide={handleCloseModal}
                remainingMedia={remainingMedia}
                onCardClick={onCardClick}
                isSignedIn={isSignedIn}
                activePlans={activePlans}
                hasFullAccess={hasFullAccess}
                freeTrialPlanIds={freeTrialPlanIds}
                mediaRating={mediaRating}
                onWatchClick={onWatchClick}
                onRatingSuccess={onRatingSuccess}
                sessionToken={sessionToken}
            />

        </>
    );
};

